import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import dynamic from 'next/dynamic';
import { makeStyles } from '@material-ui/core/styles';
import { createStructuredSelector } from 'reselect';
import get from 'lodash/get';
import { Grid } from '@material-ui/core';
import { useTranslation, Router } from '../../src/i18n';
import { getArtistsAction } from './actions';
import { selectArtists } from './selectors';
import { ARTIST_TYPE_ID, TP, LISTING_PAGE } from '../../constants/consts';
import route from '../../constants/routes';
import { selectArtistProfessionsOptions, selectVoiceTypesOptions } from '../App/selectors';
import { getFlatFormValues } from '../../utils/formUtils';
import ArtistsSearchFilters, { artistsInitialValues, artistFormFields } from '../../components/ArtistsSearchFilters';
import { useList } from '../../utils/crudUtils';
import { getRouteWithSlug } from '../../utils/common';
import ListingPageLayout from '../../components/ListingPageLayout';

// dynamic imports
const ArtistCard = dynamic(() => import('../../components/ArtistCard'));
const ListingImageCard = dynamic(() => import('../../components/ListingImageCard'));
const FbPagination = dynamic(() => import('../../components/FbPagination'));
const AppCircularProgress = dynamic(() => import('../../components/AppCircularProgress/AppCircularProgress'));
const ListingPageResultsTitle = dynamic(() => import('../../components/ListingPageResultsTitle'));

const useStyles = makeStyles(theme => ({
  titleLink: {
    paddingRight: '40px',
  },
  artistCard: {
    padding: '15px !important',
    [theme.breakpoints.down('xs')]: {
      padding: '5px !important',
    },
  },
}));

const { artistName, profession, voiceType } = artistFormFields;
export function Artists({
  artistsList,
  professions,
  voiceTypes,
  querySearchOptions,
  isPresentationMode,
  customBreadcrumbs,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [initialFormValues, setInitialValues] = useState(artistsInitialValues);

  const [artists, pagination] = useList(artistsList);

  const searchProfilesWithCriteria = values => {
    const query = getFlatFormValues(values);
    const queryString = get(query, 'query');
    Router.push({
      pathname: route.ARTISTS,
      query: { query: queryString },
    });
  };

  // criteria for search & filters have different values
  useEffect(() => {
    const initialValues = { ...artistsInitialValues };
    const options = get(artistsList, 'criteria', {});
    Object.keys(artistsInitialValues).forEach(key => {
      const value = options[key];
      switch (key) {
        case artistName:
          initialValues[key] = value || '';
          break;
        case profession: {
          const option = professions.find(prof => get(prof, 'value') === get(value, '[0]'));
          initialValues[key] = option || { value: 0, label: t('operabase.general.m_ALL'), type: '' };
          break;
        }
        case voiceType: {
          const option = voiceTypes.find(voice => get(voice, 'value') === get(value, '[0]'));
          initialValues[key] = option || { value: 0, label: t('operabase.general.m_ALL'), type: '' };
          break;
        }
        default:
          break;
      }
    });
    setInitialValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistsList, professions, voiceTypes]);

  const changePage = newPagination => {
    dispatch(
      getArtistsAction({
        type: ARTIST_TYPE_ID,
        pagination: newPagination,
        filters: querySearchOptions,
      }),
    );
  };

  const labels = {
    name: {
      title: t('operabase.general.m_ARTISTNAME'),
      property: 'name',
      className: classes.titleLink,
      linkProps: source => ({
        href: `${route.ARTISTS}/[id]/[[...action]]`,
        as: getRouteWithSlug(route.ARTISTS, source),
      }),
    },
    profession: {
      title: t('operabase.general.m_VOICEJOB'),
      property: 'profession.qualification.name',
      className: classes.titleLink,
    },
  };

  const searchFiltersComponent = (
    <ArtistsSearchFilters t={t} submitFunc={v => searchProfilesWithCriteria(v)} initialValues={initialFormValues} />
  );

  const isQuery =
    querySearchOptions.query || querySearchOptions.profession_qualification_id || querySearchOptions.profession_id;
  const content = (
    <>
      {get(artistsList, 'loading') ? (
        <AppCircularProgress />
      ) : (
        <>
          <ListingPageResultsTitle
            options={initialFormValues}
            numberResults={artistsList ? artistsList.total : 0}
            pageName={LISTING_PAGE}
          />
          <Grid container spacing={2} justify="center" style={{ paddingTop: 20 }}>
            {artists.length > 0 &&
              !isQuery &&
              artists.map(artist => (
                <Grid key={`artist-${get(artist, 'id')}`} className={classes.artistCard} item>
                  <ArtistCard artist={artist} />
                </Grid>
              ))}
          </Grid>
          <div>
            {artists.length > 0 &&
              isQuery &&
              artists.map(artist => (
                <Grid key={`artist-${get(artist, 'id')}`} item>
                  <ListingImageCard
                    source={artist}
                    labels={labels}
                    showImg
                    imageData={{
                      src: get(artist, 'image.small'),
                      alt: 'Alt text',
                    }}
                    detailsLinkProps={{
                      href: `${route.ARTISTS}/[id]/[[...action]]`,
                      as: getRouteWithSlug(route.ARTISTS, artist),
                      target: '_blank',
                    }}
                    viewDetailsText={`${t('operabase.general.m_VIEW_ARTIST')}`}
                  />
                </Grid>
              ))}
          </div>
          {artists.length === 0 && !get(artistsList, 'loading') && t(`${TP}.NO_RESULTS`)}
          {!isPresentationMode && <FbPagination filters={pagination} fetchFunction={changePage} />}
        </>
      )}
    </>
  );

  return (
    <ListingPageLayout
      title={t('operabase.general.m_ARTISTS')}
      seo={{
        title: t('operabase.general.SEO_TITLE_ARTISTS_INDEX'),
        description: t('operabase.general.SEO_DESC_ARTISTS_INDEX'),
        ogTitle: t('operabase.general.SEO_OG_TITLE_ARTISTS_INDEX'),
      }}
      subTitle={t('operabase.general.m_FINDARTISTSAROUNDWORLD')}
      content={content}
      searchFiltersComponent={searchFiltersComponent}
      customBreadcrumbs={customBreadcrumbs}
    />
  );
}

Artists.propTypes = {
  artistsList: PropTypes.object,
  querySearchOptions: PropTypes.object,
  isPresentationMode: PropTypes.bool,
};

Artists.defaultProps = {
  artistsList: {},
  querySearchOptions: {},
};

const mapStateToProps = createStructuredSelector({
  artistsList: selectArtists(),
  professions: selectArtistProfessionsOptions(),
  voiceTypes: selectVoiceTypesOptions(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(Artists);
